// Simple toast notification system
let toastContainer: HTMLDivElement | null = null

type ToastType = 'success' | 'error' | 'warning' | 'info'

function ensureContainer(): HTMLDivElement {
  if (!toastContainer) {
    toastContainer = document.createElement('div')
    toastContainer.id = 'toast-container'
    toastContainer.className = 'fixed bottom-4 right-4 z-50 flex flex-col-reverse gap-2'
    document.body.appendChild(toastContainer)
  }
  return toastContainer
}

function showToast(message: string, type: ToastType = 'info', duration: number = 3000): void {
  const container = ensureContainer()

  const toast = document.createElement('div')
  toast.className = `
    px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium
    transition-all duration-300 transform translate-x-0 opacity-100
    flex items-start gap-2 min-w-[300px] max-w-md
    ${type === 'success' ? 'bg-green-600' : ''}
    ${type === 'error' ? 'bg-red-600' : ''}
    ${type === 'warning' ? 'bg-yellow-600' : ''}
    ${type === 'info' ? 'bg-blue-600' : ''}
  `.trim().replace(/\s+/g, ' ')

  const icon = type === 'success' ? '✓' : type === 'error' ? '✕' : type === 'warning' ? '⚠' : 'ℹ'
  // Escape HTML and convert newlines to <br> tags for proper rendering
  const escapeHtml = (text: string) => text.replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m] || m))
  const formattedMessage = escapeHtml(message).replace(/\n/g, '<br>')
  toast.innerHTML = `<span class="text-lg flex-shrink-0">${icon}</span><span class="whitespace-pre-wrap">${formattedMessage}</span>`

  container.appendChild(toast)

  // Auto-remove after duration
  setTimeout(() => {
    toast.style.transform = 'translateX(400px)'
    toast.style.opacity = '0'
    setTimeout(() => {
      container.removeChild(toast)
    }, 300)
  }, duration)
}

export const toast = {
  success: (message: string, duration?: number): void => showToast(message, 'success', duration),
  error: (message: string, duration?: number): void => showToast(message, 'error', duration),
  warning: (message: string, duration?: number): void => showToast(message, 'warning', duration),
  info: (message: string, duration?: number): void => showToast(message, 'info', duration),
}
